import { yupResolver } from '@hookform/resolvers/yup'
import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import s from './CreateCreativeModal.module.scss'

import { getIconByMetaverse } from 'constants/MetaverseIcons'

import { useData } from 'contexts/CreateCreativeFormContext'

import { ClockIcon } from 'assets/icons/ClockIcon'
import { ExclamationIcon } from 'assets/icons/ExclamationIcon'
import { ImageIcon } from 'assets/icons/ImageIcon'
import { LinkIcon } from 'assets/icons/LinkIcon'
import { PlayIcon } from 'assets/icons/PlayIcon'

import { CustomDropzone } from 'components/Controls/CustomDropzone/CustomDropzone'
import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'

import { makeCreativeStep1Schema } from 'pages/MyCreatives/validation'
import { InputControlled } from 'components/Controls/Input/InputControlled'

const robloxTypes = [
  { value: 10, label: 'Image Creative' },
  { value: 11, label: 'Video Creative' },
]

interface Step1Props {
  setActivePage: () => void
  onHide: () => void
}

interface FormType {
  robloxUrl: string
  robloxSelect: { value: number; label: string }
  robloxLength: string | number
  files: File[]
}

const Step1: FC<Step1Props> = (props) => {
  const { setActivePage, onHide } = props
  const [meta, setMeta] = useState<'dcl' | 'voxel' | 'roblox' | 'unity'>('dcl')
  const { setValues } = useData()

  const [robloxCreativeForValidation, setRobloxCreativeForValidation] = useState(0)
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormType>({
    mode: 'onChange',
    resolver: yupResolver(makeCreativeStep1Schema(meta, robloxCreativeForValidation)),
    defaultValues: {
      robloxUrl: '',
      robloxSelect: robloxTypes[0],
      robloxLength: '',
      files: [] as File[],
    },
  })

  const robloxCreativeType = watch('robloxSelect')
  useEffect(() => {
    setRobloxCreativeForValidation(robloxCreativeType.value)
  }, [robloxCreativeType])

  const onSubmit = async (data: FormType) => {
    setValues({
      ...data,
      creativeType:
        meta === 'roblox' ? (robloxCreativeType.value as 10 | 11) : data.files[0].type.includes('image') ? 0 : 1,
      robloxUrl: data.robloxUrl || null,
      robloxLength: +data.robloxLength || null,
    })
    setActivePage()
  }

  return (
    <ModalFullScreen onHide={onHide} className={s.wrapper} testid="stepOneCreateCreative">
      <div className={s.headerWrapper}>
        <h1 className={s.header}> Add Creative </h1>
        <div className={s.metaverseTypeSelect}>
          <button type="button" onClick={() => setMeta('dcl')} data-testid="dcl-button">
            <img src={getIconByMetaverse('Decentraland')} alt="" className={meta === 'dcl' ? s.selected : ''} />
          </button>
          <button type="button" onClick={() => setMeta('voxel')} data-testid="voxels-button">
            <img src={getIconByMetaverse('Voxels')} alt="" className={meta === 'voxel' ? s.selected : ''} />
          </button>
          <button type="button" onClick={() => setMeta('roblox')} data-testid="roblox-button">
            <img src={getIconByMetaverse('Roblox')} alt="" className={meta === 'roblox' ? s.selected : ''} />
          </button>
          <button type="button" onClick={() => setMeta('unity')} data-testid="unity-button">
            <img src={getIconByMetaverse('Unity')} alt="" className={meta === 'unity' ? s.selected : ''} />
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={s.step1wrap} id="createCreative1step">
        <CustomDropzone
          control={control}
          name="files"
          noMediaTitle={`Drag & Drop the ${meta === 'roblox' ? 'preview' : 'Creative'} file here.`}
          // @ts-ignore
          error={errors?.files?.message}
          wrapperStyles={{ alignItems: 'center',maxWidth: 400, width: '100%', }}
          dropzoneStyles={{ height: 255, maxWidth: 400, width: '100%', borderRadius: 8 }}
          noMediaFont={18}
          iconWidth={48}
          iconHeight={48}
          testid="file-input"
        />
        {meta === 'roblox' ? (
          <div className={s.robloxControls} data-testid="roblox-fields-wrapper">
            <SelectControlled
              icon={<PlayIcon />}
              control={control}
              name="robloxSelect"
              options={robloxTypes}
              optionsIcons={{ 10: <ImageIcon />, 11: <PlayIcon /> }}
              placeholder="Select Roblox creative type"
            />
            <InputControlled
              icon={<LinkIcon />}
              placeholder="Creative link..."
              control={control}
              name="robloxUrl"
              error={errors?.robloxUrl?.message}
            />
            {robloxCreativeType.value === 11 ? (
              <InputControlled
                icon={<ClockIcon />}
                name="robloxLength"
                placeholder="Length in seconds..."
                control={control}
                error={errors?.robloxLength?.message}
              />
            ) : null}
          </div>
        ) : null}

        {meta !== 'roblox' ? (
          <ul className={s.list}>
            <li data-testid={'not-roblox-list'}>
              <ImageIcon className={s.imageIcon} />
              <span>Image files are allowed in .jpeg .jpg .png formats up to 2 MB.</span>
            </li>
            <li>
              <PlayIcon className={s.imageIcon} />
              <span>Video files are allowed in .mp4 .webm formats up to 15 MB.</span>
            </li>
            <li>
              <ExclamationIcon className={s.exclamationIcon} />
              <span>
                Be advised. All the content you upload will be sent to moderation which takes up to 48 hours. All
                inappropriate content will lead to ban.
              </span>
            </li>
          </ul>
        ) : null}
      </form>
      <div className={s.bottomWrapper}>
        {meta === 'roblox' ? (
          <div className={s.robloxExc}>
            <ul className={s.list} data-testid="roblox-list">
              <li>
                <ExclamationIcon className={s.exclamationIcon} />
                <span>
                  Be advised. All the content you upload will be sent to moderation which takes up to 48 hours. All
                  inappropriate content will lead to ban.
                </span>
              </li>
            </ul>
          </div>
        ) : null}

        <div className={s.buttonsWrapper}>
          <CustomButton type="submit" size="240" variant="primary" testid="next-button" form="createCreative1step" className={s.nextBtn}>
            Next
          </CustomButton>
          <CustomButton variant="outlined" size="152" onClick={onHide}>
            Close
          </CustomButton>
        </div>
      </div>
    </ModalFullScreen>
  )
}

export default Step1
